import React, { useContext, useEffect, useState } from "react";
import "./users-content.component.scoped.scss";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import AddNewUserModal from "../../../../dialogs/add-newUser-modal/add-new-user";
import { DataTableComponent } from "../../../../components/table/data-table/data-table.component";
import { MapperService } from "../../../../services/mapper/mapper.service";
import { UsersService } from "../../../../services/users/users.service";
import { UsersContext } from "../../../../store";
import { defaultItems } from "../../../../statics/pagination";
import moment from "moment";
import { UploadFileDialog } from "../../../../dialogs/upload-file/upload-file";

const UsersContent = () => {
  const { t } = useTranslation();
  const usersService = new UsersService();
  const mapperService = new MapperService();
  const emptyArr: any = [];
  const {
    users,
    columns,
    setUsers,
    setPagination,
    options,
    pagination,
    sort,
    setFilter,
    filter,
  } = useContext(UsersContext);

  const [showUserModal, setShowUserModal] = useState(false);
  const [showUploadFileModal, setShowUploadFileModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reset, setReset] = useState(false);
  const [counts, setCounts] = useState({
    coordinator: 0,
    internal: 0,
    external: 0,
    editor: 0,
    contributor: 0,
    auditor: 0,
    total: 0,
  });

  const fetchData = async (filter?: any) => {
    setUsers(emptyArr);
    setLoading(true);
    const usersDetails = await usersService.list(filter);
    if (!usersDetails) {
      return;
    }
    const { counts, readyUsers, paginationObj } = mapperService.mapUsers(
      usersDetails,
    );
    setUsers(readyUsers);
    setPagination(paginationObj);
    setCounts(counts);
    setLoading(false);
    setReset(false);
  };

  const refreshData = () => {
    setFilter({ role: "", searchText: "", status: null });
    setReset(true);
    const paginationParams = { items: defaultItems, page: 1 };
    fetchData(paginationParams);
  };

  const refresh = (action: string) => {
    let page = pagination.currentPage;
    if (users.length === 1 && action === "delete") {
      page = pagination.currentPage === 1 ? 1 : pagination.currentPage - 1;
    }
    const filterObj = {
      ...filter,
      page,
      items: pagination.items,
      sort: options.sortColumn,
      order: options.sortDirection,
    };
    fetchData(filterObj);
  };

  const exportData = async () => {
    const file = await usersService.exportList();
    if (file) {
      const csvContent = "data:text/csv;charset=utf-8,%EF%BB%BF";
      const downloadLink = document.createElement("a");
      downloadLink.href = csvContent + file;
      const today = moment().format("YYYY-MM-DD");
      downloadLink.download = `Users List - ${today}.csv`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const sortTable = (fieldName: string, sortDirection: string) => {
    const params = { fieldName, sortDirection };
    const filterObj = {
      ...filter,
      page: 1,
      items: pagination.items,
      sort: fieldName,
      order: sortDirection,
    };
    fetchData(filterObj);
    return sort(params);
  };

  const paginateTable = (pagination: any) => {
    const { items, currentPage } = pagination;
    const params = {
      ...filter,
      items,
      page: currentPage,
      order: options.sortDirection,
      sort: options.sortColumn,
    };
    fetchData(params);
  };

  const filterChange = (params: any) => {
    const filterObj: any = {
      items: pagination.items,
      page: 1,
      ...params,
    };
    setFilter(params);
    fetchData(filterObj);

    sort({
      fieldName: "",
      sortDirection: "",
    });
  };

  const handleModalClose = (value: any) => {
    setTimeout(() => {
      setShowUserModal(false);
    }, 0);
    if (value) {
      refreshData();
    }
  };

  const handleModalUploadFileClose = (value: any) => {
    setTimeout(() => {
      setShowUploadFileModal(false);
    }, 0);
    if (value) {
      console.log("The CSV file will import and will be back soon…");
    }
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line
  }, []);

  return (
    <Box className="users-content">
      <Box className="d-flex align-items-center justify-content-between mb-3">
        <Typography variant="body2">
          <span
            style={{ fontSize: "18px", fontWeight: 500, marginRight: "1rem" }}
          >
            {counts.total} {t("USERS")}
          </span>
          {`${counts.internal} ${t("ROLES.INTERNAL")} | 
            ${counts.external} ${t("ROLES.EXTERNAL")} | 
            ${counts.editor} ${t("ROLES.EDITOR")} | 
            ${counts.auditor} ${t("ROLES.AUDITOR")} | 
            ${counts.contributor} ${t("ROLES.CONTRIBUTOR")} | 
            ${counts.coordinator} ${t("ROLES.COORDINATOR")}`}
        </Typography>
        <Box>
          <Button
            className="btn-primary-theme mr-2"
            variant="contained"
            size="small"
            color="primary"
            onClick={() => setShowUserModal(true)}
          >
            <AddIcon /> {t("add new user")}
          </Button>
          <Button
            className="btn-primary-theme mr-2"
            variant="contained"
            size="small"
            color="primary"
            onClick={() => setShowUploadFileModal(true)}
          >
            <AddIcon /> {t("IMPORT_NEW_USERS")}
          </Button>
          <Button
            className="btn-primary-theme"
            variant="outlined"
            size="small"
            color="primary"
            onClick={exportData}
          >
            {t("USERS_PAGE.EXPORT_USERS")}
          </Button>
        </Box>
      </Box>
      <DataTableComponent
        tableName="users"
        loading={loading}
        reset={reset}
        columns={columns}
        data={users}
        options={options}
        sort={sortTable}
        pagination={pagination}
        paginate={paginateTable}
        title={t("USERS_LIST")}
        subTitle={""}
        reload={false}
        onReload={refreshData}
        exportData={true}
        onExport={exportData}
        filter={filter}
        onFilterChange={filterChange}
        refresh={refresh}
        clickableRow={false}
      />

      {showUserModal && (
        <AddNewUserModal
          show={showUserModal}
          editable={false}
          onClose={(value: any) => handleModalClose(value)}
        />
      )}
      {showUploadFileModal && (
        <UploadFileDialog
          show={showUploadFileModal}
          onClose={(value: any) => handleModalUploadFileClose(value)}
        />
      )}
    </Box>
  );
};

export default UsersContent;
